/**
 * Reload the page without interrupting the user.
 *
 * A service-worker update (controllerchange) or a build-version skew must NEVER
 * reload mid-interaction. On mobile the login ceremony (on-screen keyboard,
 * browser autofill, password-manager / biometric sheet, app-switching) fires
 * focus / pageshow / visibilitychange constantly; an immediate reload there
 * aborts the in-flight `oauth/token` request, which surfaces as a generic
 * "network error" on essentially every mobile login. Desktop has no such event
 * storm, so it was unaffected — hence the mobile-only breakage.
 *
 * Fix: reload immediately only when the tab is already hidden; otherwise wait
 * until it becomes hidden (tab switch / app background). The user picks up the
 * new build the next time they return, never mid-flow.
 *
 * ...with one exception: a hidden tab is NOT proof the user is idle. The auth
 * ceremony routinely hides the page while the user is very much mid-flow —
 * the WebAuthn/passkey sheet (Touch ID, Windows Hello, cross-device QR) is a
 * native modal that backgrounds the tab, and typed-code 2FA sends the user to
 * their mail app for the code. Reloading there wipes the login form's in-flight
 * state, so the sign-in silently dies and the user has to log in a second time
 * (reported on acceptance, which redeploys often enough that a reload is armed
 * on nearly every visit). While on an auth page we therefore keep waiting
 * instead of reloading — a stale build still self-heals the next time the tab
 * hides once the user is past the ceremony.
 */

import { isOnLoginRoute } from '@/repositories/clients/sharedTokenRefresh';

let scheduled = false;

export function reloadWhenIdle(reload = () => window.location.reload()) {
  if (scheduled) return;
  scheduled = true;

  if (typeof document === 'undefined') {
    reload();
    return;
  }

  const reloadWhenSafe = () => {
    if (document.visibilityState !== 'hidden') return false;
    // Mid sign-in (passkey sheet / 2FA code lookup) — stay armed, don't reload.
    if (isOnLoginRoute()) return false;
    reload();
    return true;
  };

  if (reloadWhenSafe()) return;

  const onHidden = () => {
    if (reloadWhenSafe()) {
      document.removeEventListener('visibilitychange', onHidden);
    }
  };
  document.addEventListener('visibilitychange', onHidden);
}

// ponytail: test-only reset for the module-level guard.
export function _resetForTests() {
  scheduled = false;
}
